import { 
  CategoryData, 
  EpworthResponseType, 
  FamilyHistoryData, 
  MedicationsData, 
  PastMedicalHistoryData, 
  Provider, 
  ProviderHistoryData, 
  ReviewOfSystemsData, 
  SleepMedicineHistoryData, 
  SocialHistoryData,
  WeightHealthData,
  WeightHistoryData,
  PaperworkCategoryType,
  WeightManagementCategoryType,
  WeightTreatmentHistoryData,
  ExerciseHistoryData,
  EatingHabitsData,
  MentalHealthHistoryData,
  OtherWeightManagementHistoryData,
  MedicationFeedbackData,
  WeightLossProgressData,
  WMFollowUpCategoryType
} from "../types/PatientFormTypes";


export const defaultProviderData: Provider = {
  providerName: '',
  credentials: '',
  practicingCity: '',
  careStartDate: new Date(),
  careEndDate: null
};

// Provider History
export const defaultProviderHistoryData: ProviderHistoryData = {
  primaryCareProvider: defaultProviderData,
  hasReferringProvider: false,
  isSameAsPrimaryCare: null,
  referringProvider: null
};

// Medications
export const defaultMedicationsData: MedicationsData = {
  takingMedications: false,
  activeMedications: [],
  allergicToMedications: false,
  medicationAllergies: []
};

// Past Medical History
export const generalMedicalProblemsData: string[] = [
  "Anesthesia Complications",
  "Anxiety, Depression or Mental Illness",
  "Blood Problems (Anemia, High/low White count)",
  "Diabetes",
  "High Blood Pressure",
  "High Cholesterol or Triglycerides",
  "Stroke or TIA",
];

export const specificMedicalProblemsData: string[] = [
  "Eyes (Cataracts, Glaucoma)",
  "Ears, Nose Sinuses, Tonsils",
  "Thyroid, Parathyroid Glands",
  "Heart Valves, Heart Rhythm, Heart Failure",
  "Coronary (Heart), Arteries (Angina, Heart Attack)",
  "Arteries (Aorta, Arteries to head, arms, legs)",
  "Veins, Blood clots in veins",
  "Lungs",
  "Esophagus or Stomach (Ulcer)",
  "Bowel (Small or large intestine)",
  "Appendix",
  "Liver or Gallbladder",
  "Hernia",
  "Kidneys, Bladder",
  "Bones, Joints, Muscles",
  "Back, Neck, Spine",
  "Brain",
  "Skin",
  "Breasts",
  "Uterus, Tubes, Ovaries ",
  "Prostate, Penis, Testes, Vasectomy ",
  "Other (Please describe)",
];

export const defaultPastMedicalHistoryData: PastMedicalHistoryData = {
  hasPaceMaker: false,
  generalMedicalProblems: [],
  specificMedicalProblems: [],
};

// Social History
export const socialStatusesData: { value: string, description: string }[] = [
  { value: "employed", description: "I am currently employed" },
  { value: "exercise", description: "I exercise" },
  { value: "caffeinate", description: "I consume caffeinated beverages" },
  { value: "alcohol", description: "I consume alcoholic beverages" },
  { value: "tobacco", description: "I have used tobacco products" },
  { value: "substance abuse treatment", description: "I have undergone treatment for substance abuse" },
];

export const defaultSocialHistoryData: SocialHistoryData = {
  maritalStatus: 'Married',
  socialStatuses: [],
};

export const defaultFamilyHistoryData: FamilyHistoryData = {
  badSleep: { response: false, description: '' },
  familyMedicalConditions: []
};

// Sleep Medicine History
export const PersonalSleepHistoryOptions = [
	{ value: "drowsyDay", label: "Feel very drowsy or sleepy at any point during the day" },
	{ value: "hallucinationDreams", label: "Have strange hallucination-like dreams while napping" },
	{ value: "sleepAttacks", label: `Have "sleep attacks" during the day (i.e., periods when you cannot prevent yourself from falling asleep)` },
	{ value: "cataplecticAttacks", label: `Have "cataplectic attacks" (i.e., episodes of weakness in the legs and/or collapse that occur with emotions like laughter or crying)` },
	{ value: "sleepParalysis", label: "Have episodes of sleep paralysis (i.e., being awake in bed not able to move or speak)" },
	{ value: "legRestlessness", label: `Wake up with "pins and needles" or restlessness in the legs` },
];

export const epworthSleepinessScaleOptions = [
	{ name: "reading", label: "Sitting and reading" },
	{ name: "watchingTV", label: "Watching TV" },
	{ name: "inactivePublic", label: "Sitting inactive in a public place" },
	{ name: "passenger", label: "Sitting for an hour as a passenger in a car" },
	{ name: "afternoonLayDown", label: "Lying down in the afternoon to rest" },
	{ name: "talking", label: "Sitting & talking to another person" },
	{ name: "afterLunch", label: "Sitting quietly after lunch (no alcohol)" },
	{ name: "inTraffic", label: "Sitting in a car stopped in traffic" },
];

export const epworthKeys = ["reading", "watchingTV", "inactivePublic", "passenger", "afternoonLayDown", "talking", "afterLunch", "inTraffic"] as const;
export const defaultSleepMedicineHistoryData: SleepMedicineHistoryData = {
  pastSleepStudy: { response: false, description: '', document: null },
  isOnPAP: false,
  papVendor: '',
  drowsinessAccident: { response: false, description: '' },
  sleepQuestionnaire: {
    averageHoursSlept: '',
    averageMinutesToDoze: '',
    numberOfAwakenings: '',
    sleepBehaviors: [],
    personalSleepHistory: [],
    averageBedtime: '',
    averageRiseTime: '',
    sleepStatus: '',
  },
  epworthSleepinessScale: {
    reading: 'None',
    watchingTV: 'None',
    inactivePublic: 'None',
    passenger: 'None',
    afternoonLayDown: 'None',
    talking: 'None',
    afterLunch: 'None',
    inTraffic: 'None',
    score: 0,
  }
};

export const epworthScoreMap: Record<EpworthResponseType, number> = {
  None: 0,
  Slight: 1,
  Moderate: 2,
  High: 3,
};

export const defaultWeightHealthData: WeightHealthData = {
  botheredByWeight: 'Very often',
  discussWeight: false,
  weightLossMethods: [],
  eatingDisorder: null,
  pregnant: null,
  pregnantPlanning: null
};

// Review of Systems
export const defaultReviewOfSystemsData: ReviewOfSystemsData = {
  unit: 'Imperial',
  height: { feet: null, inches: null, cm: null },
  weight: { pounds: null, kg: null },
  recentSymptoms: [],
  accidentRelated: {
    date: '',
    accidentInsurance: '',
    employer: '',
    accident: 'No',
  }
};

export const defaultWeightHistoryData: WeightHistoryData = {
  weightGainStart: '',
  weightGainInfluence: [],
  weightTrend: '',
  goalWeight: '',
  goalWeightUnit: 'lbs',
  weightGoalDescription: [],
};

export const defaultWeightTreatmentHistoryData: WeightTreatmentHistoryData = {
  medications: [],
  lifestylePrograms: [],
  priorSurgeriesOrProcedures: [],
  logging: [],
};

export const defaultExerciseHistoryData: ExerciseHistoryData = {
  exercises: [],
};

export const defaultEatingHabitsData: EatingHabitsData = {
  mealsAndSnacks: '',
  eatingBehaviors: [],
  foodAndDrink: '',
  eatingDisorders: [],
};

export const defaultMentalHealthHistoryData: MentalHealthHistoryData = {
  mentalHealthConcern: [],
  motivationAndReadiness: '',
};

export const defaultOtherWeightManagementHistoryData: OtherWeightManagementHistoryData = {
  medicalHistory: [],
  type2Diabetes: false,
  diabetesMedications: [],
  checkBloodSugar: null,
  eyeDisease: { response: false, description: '' },
  gastrointestinalProblems: [],
  stomachOrBowelSurgery: { response: false, description: '' },
  upcomingSurgeries: { response: false, description: '' },
  femaleHistory: [],
};

export const defaultMedicationFeedbackData: MedicationFeedbackData = {
  medicationFeedback: '',
  medicationSymptoms: [],
};

export const defaultWeightLossProgressData: WeightLossProgressData = {
  weightLossProgress: '',
  changesInSleep: { response: false, description: '' },
  additionalComments: '',
};

// Patient Details
export const defaultPaperworkBackgroundData: Record<PaperworkCategoryType, CategoryData> = {
  providerHistory: defaultProviderHistoryData,
  medications: defaultMedicationsData,
  pastMedicalHistory: defaultPastMedicalHistoryData,
  socialHistory: defaultSocialHistoryData,
  familyHistory: defaultFamilyHistoryData,
  sleepMedicineHistory: defaultSleepMedicineHistoryData,
  weightHealth: defaultWeightHealthData,
  reviewOfSystems: defaultReviewOfSystemsData
};

export const defaultWeightManagementData: Record<WeightManagementCategoryType, CategoryData> = {
  weightHistory: defaultWeightHistoryData,
  weightTreatmentHistory: defaultWeightTreatmentHistoryData,
  exerciseHistory: defaultExerciseHistoryData,
  eatingHabits: defaultEatingHabitsData,
  mentalHealthHistory: defaultMentalHealthHistoryData,
  otherWeightManagementHistory: defaultOtherWeightManagementHistoryData,
  medicationFeedback: defaultMedicationFeedbackData,
  weightLossProgress: defaultWeightLossProgressData
};

export const defaultWMFollowUpData: Record<WMFollowUpCategoryType, CategoryData> = {
  reviewOfSystems: defaultReviewOfSystemsData,
  medications: defaultMedicationsData,
  medicationFeedback: defaultMedicationFeedbackData,
  exerciseHistory: defaultExerciseHistoryData,
  weightLossProgress: defaultWeightLossProgressData,
};

export const formNameMap: Record<string, string> = {
  "newPatient": "New Patient Forms",
  "followUp": "Follow-Up Forms",
  "weightManagement": "Weight Management Questionnaire",
  "wmFollowUp": "Weight Management Follow-Up Form",
  "generic": "Medical History Form",
};